/* ===================================================================
   Native Possum — np-browse.js
   The browse surface (snippets/np-browse.liquid): collection pages and
   search results. Works on both because the root element carries its own
   engine URL, collection and search terms; the drawer's form carries every
   parameter the page needs, so one serialisation is the whole truth.

   PROGRESSIVE ENHANCEMENT: every control already works without this file —
   the drawer is a GET form, chips and page numbers are links, the Filters
   link opens the drawer with :target. This file makes them work in place.

   WHAT IT ADDS
   - Drawer: opens as a layer (np-utilities npLayer: focus in and back,
     Escape, everything behind inert). Collection pages ship it empty; its
     groups are fetched from the engine the first time it opens.
   - Live counts: ticking an option asks the engine again and updates every
     count and the "Show N products" button before the drawer is closed.
   - Results in place: one response replaces everything inside
     [data-np-browse] (count, chips, grid, page numbers) and the address bar
     keeps /collections/<handle>?f.colour=… — shareable, Back works.
   - Search: pages and journal posts from Shopify's own search, under the
     products.
   Announces result counts in the live region (SC 4.1.3). Fires
   'np:browse:updated' after every swap (np-card.js listens).
   =================================================================== */

(function () {
  'use strict';

  if (window.npBrowseReady) return;
  window.npBrowseReady = true;

  var root = document.querySelector('[data-np-browse]');
  if (!root) return;

  /* ---------------------------------------------------------- announce */
  function announce(message) {
    var live = root.querySelector('[data-np-live]');
    if (live) live.textContent = message;
  }

  /* ------------------------------------------------------------ engine */
  /* ON A COLLECTION THE ENGINE ANSWERS at /apps/browse?collection=…, which
     is where every chip, page link and the drawer's form point, so they
     work with this script off. With it on the engine is fetched and the
     address bar keeps /collections/<handle>. These two functions are the
     whole mapping between the two addresses.

     A SEARCH IS NEVER MAPPED BACK: /search is Shopify's own search, and
     sections/main-search.liquid forwards it to the engine. */
  var ENGINE_PARAM = /^(q|exact|f\..+|price\.gte|price\.lte|available|sort|page)$/;
  var ENGINE_STATE = /^(q|exact|f\..+|price\.gte|price\.lte|available|sort)$/;

  function engineUrl() { return root.dataset.engineUrl || '/apps/browse'; }
  function isSearch() { return root.dataset.context === 'search'; }

  function onEnginePath(href) {
    return new URL(href, location.origin).pathname.indexOf(engineUrl()) === 0;
  }

  function hasEngineState(href) {
    var found = false;
    new URL(href, location.origin).searchParams.forEach(function (v, k) {
      if (ENGINE_STATE.test(k) && v !== '') found = true;
    });
    return found;
  }

  /* append, never set: two ticked colours are two f.colour fields. */
  function toEngine(href) {
    var from = new URL(href, location.origin);
    var to = new URL(engineUrl(), location.origin);
    if (root.dataset.collection) to.searchParams.set('collection', root.dataset.collection);
    from.searchParams.forEach(function (v, k) {
      if (ENGINE_PARAM.test(k)) to.searchParams.append(k, v);
    });
    return to.pathname + to.search;
  }

  function toStorefront(href) {
    var from = new URL(href, location.origin);
    var to = new URL(root.dataset.storefrontUrl || location.pathname, location.origin);
    from.searchParams.forEach(function (v, k) {
      if (ENGINE_PARAM.test(k) || k === 'view') to.searchParams.append(k, v);
    });
    return to.pathname + to.search;
  }

  /* What the address bar shows for an engine URL. */
  function shownUrl(href) {
    return isSearch() || !root.dataset.storefrontUrl ? href : toStorefront(href);
  }

  function parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /* One cache for every engine page this visit asked for: the live counts
     and the Show button ask for the same URL, so pressing Show is instant. */
  var cache = Object.create(null);
  function getPage(url, signal) {
    if (cache[url]) return Promise.resolve(cache[url]);
    return fetch(url, { signal: signal, credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Browse request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        cache[url] = html;
        return html;
      });
  }

  /* ------------------------------------------------------------ drawer */
  function drawer() { return root.querySelector('[data-np-facets]'); }
  function scrim() { return root.querySelector('[data-np-scrim]'); }
  function opener() { return root.querySelector('[data-np-filter-open]'); }

  function drawerStatus(message) {
    var panel = drawer();
    var status = panel && panel.querySelector('[data-np-drawer-status]');
    if (status) status.textContent = message;
  }

  /* A collection page ships the drawer empty: its groups come from the
     engine, fetched the first time it opens, for whatever is showing. */
  function fillDrawer() {
    var panel = drawer();
    if (!panel || !panel.dataset.npLazy) return;
    var fallback = panel.dataset.npLazy;
    delete panel.dataset.npLazy;
    getPage(toEngine(location.href))
      .then(function (html) {
        var doc = parse(html);
        if (doc.querySelector('[data-np-unavailable]')) {
          drawerStatus('Filters aren’t available for this collection.');
          return;
        }
        var next = doc.querySelector('[data-np-facets]');
        if (!next || next.dataset.npLazy) throw new Error('Engine drawer not found');
        panel.innerHTML = next.innerHTML;
        // The engine's title has its own id: keep the drawer named by it
        var title = panel.querySelector('.np-facets__title');
        if (title && title.id) panel.setAttribute('aria-labelledby', title.id);
        if (openedWith === null) openedWith = formUrl();
        paintSelected();
        setupAccordions();
        if (panel.dataset.open === 'true') {
          var close = panel.querySelector('[data-np-filter-close]');
          if (close) close.focus();
        }
      })
      .catch(function () {
        /* The engine page draws the drawer open. */
        location.href = fallback + '#np-facets-engine';
      });
  }

  /* CLOSING APPLIES. What the drawer showed when it opened; closing it
     any way (×, Escape, the dimmed page) with different ticks loads those
     ticks, so "Show N products" is a shortcut, not a requirement. */
  var openedWith = null;

  function openDrawer(trigger) {
    var panel = drawer();
    if (!panel || panel.dataset.open === 'true') return;
    openedWith = root.querySelector('[data-np-facet-form]') ? formUrl() : null;
    fillDrawer();
    panel.dataset.open = 'true';
    scrim().hidden = false;
    document.documentElement.classList.add('np-browse-locked');
    window.npLayer.open(panel, {
      trigger: trigger || opener(),
      backdrop: scrim(),
      initialFocus: '[data-np-filter-close]',
      onRequestClose: function () { closeDrawer(true); }
    });
  }

  function closeDrawer(apply) {
    var panel = drawer();
    if (!panel || panel.dataset.open !== 'true') return;
    panel.dataset.open = 'false';
    scrim().hidden = true;
    document.documentElement.classList.remove('np-browse-locked');
    window.npLayer.close(panel);
    if (apply && root.querySelector('[data-np-facet-form]')) {
      var now = formUrl();
      if (openedWith !== null && now !== openedWith) loadResults(now);
    }
    openedWith = null;
  }

  /* The names ticked in each group, beside its heading ("Green, Blue"). */
  function paintSelected() {
    root.querySelectorAll('[data-np-facet]').forEach(function (group) {
      var names = [];
      group.querySelectorAll('input:checked').forEach(function (input) {
        var label = input.closest('label');
        var name = label && label.querySelector('.np-swatch__name, .np-size-opt__name, .np-check__name');
        if (name) names.push(name.textContent.trim());
      });
      var slot = group.querySelector('[data-np-selected]');
      if (slot) slot.textContent = names.join(', ');
      var badge = group.querySelector('[data-np-badge]');
      if (badge) {
        badge.hidden = names.length === 0;
        badge.innerHTML = names.length ? names.length + '<span class="np-visually-hidden"> selected</span>' : '';
      }
    });
  }

  /* ---------------------------------------------------- phone sort sheet */
  /* SORT in the phone bar opens a short list of sort orders; picking one
     loads it straight away, keeping every filter. */
  function sortSheet() { return root.querySelector('[data-np-sort-sheet]'); }
  function sortScrim() { return root.querySelector('[data-np-sort-scrim]'); }

  function openSortSheet(trigger) {
    var sheet = sortSheet();
    if (!sheet || sheet.dataset.open === 'true') return;
    sheet.dataset.open = 'true';
    sortScrim().hidden = false;
    document.documentElement.classList.add('np-browse-locked');
    window.npLayer.open(sheet, {
      trigger: trigger,
      backdrop: sortScrim(),
      initialFocus: '[aria-current="true"]',
      onRequestClose: closeSortSheet
    });
  }

  function closeSortSheet() {
    var sheet = sortSheet();
    if (!sheet || sheet.dataset.open !== 'true') return;
    sheet.dataset.open = 'false';
    sortScrim().hidden = true;
    document.documentElement.classList.remove('np-browse-locked');
    window.npLayer.close(sheet);
  }

  function sortUrl(value) {
    var url = new URL(formUrl(), location.origin);
    url.searchParams.delete('page');
    if (value) url.searchParams.set('sort', value);
    else url.searchParams.delete('sort');
    return url.pathname + url.search;
  }

  /* ---------------------------------------------------- phone accordions */
  /* On phones each filter group is collapsed behind its heading, as on the
     old collection pages; one with something ticked starts open. Elsewhere
     the heading button is switched off and every group is open. */
  var phone = window.matchMedia('(max-width: 768px)');

  function setupAccordions() {
    root.querySelectorAll('[data-np-facet]').forEach(function (group) {
      var toggle = group.querySelector('[data-np-facet-toggle]');
      if (!toggle) return;
      if (phone.matches) {
        toggle.disabled = false;
        toggle.removeAttribute('role');
        if (!toggle.hasAttribute('aria-expanded')) {
          setGroupOpen(group, !!group.querySelector('input:checked'));
        }
      } else {
        toggle.disabled = true;
        toggle.setAttribute('role', 'none');
        toggle.removeAttribute('aria-expanded');
        group.classList.remove('is-collapsed');
      }
    });
  }

  function setGroupOpen(group, open) {
    var toggle = group.querySelector('[data-np-facet-toggle]');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    group.classList.toggle('is-collapsed', !open);
  }

  if (phone.addEventListener) phone.addEventListener('change', setupAccordions);

  /* -------------------------------------------------------- the form */
  /* The base is a bare path: everything the URL needs — including the
     search terms — is a field in the form. Empty values (the default sort)
     are dropped so the URL stays as short as the state. */
  function formUrl() {
    var form = root.querySelector('[data-np-facet-form]');
    if (!form) return toEngine(location.href);
    var params = new URLSearchParams(new FormData(form));
    Array.from(params.keys()).forEach(function (key) {
      if (params.get(key) === '') params.delete(key);
    });
    var base = engineUrl().split('?')[0];
    var query = params.toString();
    return query ? base + '?' + query : base;
  }

  /* Live counts: the drawer stays open, and every count in it — and the
     button — says what pressing Show will give. */
  var countTimer = null;
  var countRequest = null;

  function refreshCounts() {
    clearTimeout(countTimer);
    countTimer = setTimeout(function () {
      if (countRequest) countRequest.abort();
      countRequest = new AbortController();
      /* The button stays full colour and says what it is doing; the
         count comes back in its place. */
      var apply = root.querySelector('[data-np-apply]');
      var before = apply ? apply.textContent : '';
      if (apply) {
        apply.setAttribute('aria-busy', 'true');
        apply.textContent = 'Updating\u2026';
      }
      getPage(formUrl(), countRequest.signal)
        .then(function (html) {
          countRequest = null;
          var doc = parse(html);
          var nextApply = doc.querySelector('[data-np-apply]');
          if (apply) {
            apply.removeAttribute('aria-busy');
            if (nextApply) apply.textContent = nextApply.textContent.trim();
          }
          syncOptions(doc);
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          if (apply) {
            apply.removeAttribute('aria-busy');
            apply.textContent = before;
          }
        });
    }, 250);
  }

  /* The drawer's options become the ones the engine just sent, as the page
     would draw them after Show: an option the other ticks emptied goes, one
     they brought back returns, a ticked one always stays (the engine sends
     it). Options are matched by input id, which the engine builds from the
     value's key, so a count can never land on a neighbour. Nothing that is
     staying is moved or rebuilt, so the box just clicked keeps its focus. */
  function syncOptions(doc) {
    var body = root.querySelector('.np-facets__body');
    if (!body) return;
    var incoming = doc.querySelectorAll('[data-np-facet]');
    var seen = {};
    incoming.forEach(function (nextGroup) {
      var key = nextGroup.getAttribute('data-np-facet');
      seen[key] = true;
      var group = body.querySelector('[data-np-facet="' + CSS.escape(key) + '"]');
      if (!group) {
        /* A group the page had nothing for: placed before the next group
           both renders share, or last. The click handler is delegated. */
        var anchor = null;
        for (var sib = nextGroup.nextElementSibling; sib && !anchor; sib = sib.nextElementSibling) {
          var k = sib.getAttribute('data-np-facet');
          if (k) anchor = body.querySelector('[data-np-facet="' + CSS.escape(k) + '"]');
        }
        body.insertBefore(document.importNode(nextGroup, true), anchor);
        return;
      }
      group.hidden = false;
      var values = group.querySelector('.np-facet__values');
      var wanted = {};
      var previous = null;
      nextGroup.querySelectorAll('.np-facet__values > label').forEach(function (nextLabel) {
        var id = nextLabel.getAttribute('for');
        wanted[id] = true;
        var input = document.getElementById(id);
        var label = input && group.contains(input) ? input.closest('label') : null;
        if (label) {
          label.hidden = false;
          var count = label.querySelector('[data-np-count-for]');
          var nextCount = nextLabel.querySelector('[data-np-count-for]');
          if (count && nextCount) count.innerHTML = nextCount.innerHTML;
        } else {
          label = document.importNode(nextLabel, true);
          values.insertBefore(label, previous ? previous.nextSibling : values.firstChild);
        }
        previous = label;
      });
      values.querySelectorAll(':scope > label').forEach(function (label) {
        if (!wanted[label.getAttribute('for')]) label.hidden = true;
      });
    });
    body.querySelectorAll('[data-np-facet]').forEach(function (group) {
      if (!seen[group.getAttribute('data-np-facet')]) group.hidden = true;
    });
    setupAccordions();
  }

  /* ---------------------------------------------------- results in place */
  var pending = null;

  function loadResults(url, options) {
    options = options || {};
    if (pending) pending.abort();
    var controller = new AbortController();
    pending = controller;

    var engine = onEnginePath(url) ? url : toEngine(url);
    root.setAttribute('aria-busy', 'true');
    var grid = root.querySelector('#npProductGrid');
    if (grid) grid.classList.add('np-loading');
    announce('Loading products');

    return getPage(engine, controller.signal)
      .then(function (html) {
        var next = parse(html).querySelector('[data-np-browse]');
        if (!next) throw new Error('Browse markup not found in response');
        pending = null;
        root.innerHTML = next.innerHTML;
        root.removeAttribute('aria-busy');

        if (!options.replace) history.pushState({ np: true }, '', shownUrl(engine));
        wire();

        var count = root.querySelector('[data-np-count]');
        announce(count ? count.textContent.replace(/\s+/g, ' ').trim() : 'Products updated');

        if (options.toTop) {
          root.scrollIntoView({ block: 'start' });
          var newGrid = root.querySelector('#npProductGrid');
          if (newGrid) newGrid.focus({ preventScroll: true });
        }
        /* A "View all" address (shared, or Back to one) asks for the rest. */
        if (isViewAll(location.href)) return viewAll();
      })
      .catch(function (error) {
        if (error.name === 'AbortError') return;
        /* Never leave the shopper on a stale grid: a real navigation is
           what would have happened without this file. */
        location.href = engine;
      });
  }

  /* ------------------------------------------------------------ view all */
  /* THE PAGES STAY 18 LONG and "View all" adds the others to this grid:
     the engine's pages are drawn by Shopify, which resolves about twenty
     products a page, so one page of everything is not something it can
     draw. Every other page is fetched (three at a time) into a slot kept
     in page order, each slot showing shimmer cards until its page lands.
     The address gets ?view=all, so the link can be shared and Back
     returns to it; any new filter or sort starts again from page 1. */
  var VIEW_ALL_AT_ONCE = 3;

  function isViewAll(href) {
    return new URL(href, location.origin).searchParams.get('view') === 'all';
  }

  function viewAllUrl(href) {
    var u = new URL(href, location.origin);
    u.searchParams.delete('page');
    u.searchParams.set('view', 'all');
    return u.pathname + u.search;
  }

  function placeholders(count) {
    var html = '';
    for (var i = 0; i < count; i++) {
      html += '<div class="np-product-card is-placeholder" aria-hidden="true">' +
        '<div class="np-product-image-wrapper"></div><div class="np-product-info">' +
        '<div class="np-product-brand">&nbsp;</div><div class="np-product-name">&nbsp;</div>' +
        '<div class="np-product-price">&nbsp;</div></div></div>';
    }
    return html;
  }

  /* Page n's address: the engine's own, or Shopify's collection page with
     just this section (the unfiltered page Shopify draws). */
  function pageUrl(base, n) {
    var u = new URL(base + n, location.origin);
    if (!onEnginePath(u.href)) u.searchParams.set('section_id', root.dataset.sectionId);
    return u.pathname + u.search;
  }

  function viewAll(options) {
    options = options || {};
    var nav = root.querySelector('[data-np-pagination]');
    var grid = root.querySelector('#npProductGrid');
    if (!nav || !grid) return Promise.resolve();

    var pages = parseInt(nav.dataset.npPages, 10);
    var current = parseInt(nav.dataset.npCurrent, 10);
    var size = parseInt(nav.dataset.npPageSize, 10);
    var total = parseInt(nav.dataset.npTotal, 10);
    var base = nav.dataset.npPageBase;

    /* One slot per page, in page order; the cards already here are the
       current page's. display: contents keeps every card in the grid. */
    var slots = {};
    var existing = Array.prototype.slice.call(grid.children);
    grid.innerHTML = '';
    for (var n = 1; n <= pages; n++) {
      var slot = document.createElement('div');
      slot.style.display = 'contents';
      if (n === current) {
        existing.forEach(function (card) { slot.appendChild(card); });
      } else {
        slot.innerHTML = placeholders(n === pages ? total - size * (pages - 1) : size);
      }
      slots[n] = slot;
      grid.appendChild(slot);
    }

    nav.outerHTML = '<p class="np-pagination-context np-pagination-context--all">Showing all ' + total + ' products</p>';
    if (options.push) history.pushState({ np: true }, '', viewAllUrl(location.href));
    announce('Loading all ' + total + ' products');

    var queue = [];
    for (var p = 1; p <= pages; p++) if (p !== current) queue.push(p);

    function fill(n) {
      return getPage(pageUrl(base, n))
        .then(function (html) {
          var next = parse(html).querySelector('#npProductGrid');
          slots[n].innerHTML = next ? next.innerHTML : '';
          document.dispatchEvent(new CustomEvent('np:browse:updated', { detail: { root: slots[n] } }));
        })
        .catch(function () { slots[n].innerHTML = ''; });
    }

    function worker() {
      var n = queue.shift();
      return n ? fill(n).then(worker) : Promise.resolve();
    }

    var workers = [];
    for (var w = 0; w < VIEW_ALL_AT_ONCE; w++) workers.push(worker());
    return Promise.all(workers).then(function () {
      announce('Showing all ' + total + ' products');
    });
  }

  /* ------------------------------------------------ search: pages & journal */
  var content = null;

  function fillContent() {
    var terms = root.dataset.searchTerms;
    var section = root.querySelector('[data-np-content-results]');
    if (!isSearch() || !terms || !section) return;
    var list = section.querySelector('[data-np-content-list]');
    var render = function (items) {
      if (!items.length) return;
      list.innerHTML = items.map(function (item) {
        var article = item.url.indexOf('/blogs/') !== -1;
        return '<li class="predictive-content-item"><a class="predictive-content-link" href="' +
          window.npEscapeHtml(item.url) + '"><span class="predictive-content-badge">' +
          (article ? 'Journal' : 'Page') + '</span><span class="predictive-content-title">' +
          window.npEscapeHtml(item.title) + '</span></a></li>';
      }).join('');
      section.hidden = false;
    };
    if (content) { render(content); return; }
    var url = (window.Shopify && Shopify.routes ? Shopify.routes.root : '/') +
      'search/suggest.json?q=' + encodeURIComponent(terms) +
      '&resources[type]=page,article&resources[limit]=4';
    fetch(url)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var res = data && data.resources && data.resources.results;
        content = res ? (res.pages || []).concat(res.articles || []) : [];
        render(content);
      })
      .catch(function () {});
  }

  /* -------------------------------------------------------------- wiring */
  function wire() {
    /* The Filter control is a link so it works with the script off; now
       it is a button in every sense a screen reader cares about. */
    root.querySelectorAll('[data-np-filter-open], [data-np-sort-open]').forEach(function (open) {
      open.setAttribute('role', 'button');
      open.setAttribute('aria-expanded', 'false');
      if (open.dataset.controls) open.setAttribute('aria-controls', open.dataset.controls);
    });
    var sheet = sortSheet();
    if (sheet && sheet.dataset.open !== 'true') sheet.inert = true;
    var panel = drawer();
    if (panel && panel.dataset.open !== 'true') panel.inert = true;
    var all = root.querySelector('[data-np-view-all]');
    if (all) all.hidden = false;
    paintSelected();
    setupAccordions();
    fillContent();
    document.dispatchEvent(new CustomEvent('np:browse:updated', { detail: { root: root } }));
  }

  /* Delegated, so it survives the contents being replaced. */
  root.addEventListener('click', function (event) {
    var t = event.target;

    var openLink = t.closest('[data-np-filter-open]');
    if (openLink) { event.preventDefault(); openDrawer(openLink); return; }

    var facetToggle = t.closest('[data-np-facet-toggle]');
    if (facetToggle && !facetToggle.disabled) {
      var group = facetToggle.closest('[data-np-facet]');
      setGroupOpen(group, group.classList.contains('is-collapsed'));
      return;
    }

    var sortOpen = t.closest('[data-np-sort-open]');
    if (sortOpen) { event.preventDefault(); openSortSheet(sortOpen); return; }

    var sortPick = t.closest('[data-np-sort]');
    if (sortPick) {
      var url = sortUrl(sortPick.dataset.npSort);
      closeSortSheet();
      if (sortPick.getAttribute('aria-current') !== 'true') loadResults(url, { toTop: true });
      return;
    }

    if (t.closest('[data-np-filter-close]')) {
      event.preventDefault();
      closeDrawer(true);
      return;
    }

    var facetLink = t.closest('[data-np-facet-link]');
    if (facetLink) {
      event.preventDefault();
      closeDrawer();
      loadResults(facetLink.href);
      return;
    }

    if (t.closest('[data-np-view-all]')) {
      viewAll({ push: true });
      return;
    }

    var pageLink = t.closest('[data-np-page]');
    if (pageLink) {
      event.preventDefault();
      loadResults(pageLink.href, { toTop: true });
      return;
    }

    /* Return-to-results: Back from a product lands where they left. */
    if (t.closest('[data-np-card]') && !t.closest('[data-wishlist-save]')) {
      try {
        sessionStorage.setItem('np:return', JSON.stringify({ url: location.href, y: window.scrollY }));
      } catch (e) {}
    }
  });

  root.addEventListener('change', function (event) {
    if (!event.target.closest('[data-np-facet-form]')) return;
    paintSelected();
    refreshCounts();
  });

  root.addEventListener('submit', function (event) {
    if (!event.target.closest('[data-np-facet-form]')) return;
    event.preventDefault();
    var url = formUrl();
    closeDrawer();
    loadResults(url);
  });

  window.addEventListener('popstate', function () {
    var href = onEnginePath(location.href) ? location.href : toEngine(location.href);
    loadResults(href, { replace: true });
  });

  wire();

  /* iOS keeps a toolbar at the bottom that slides in and out; keep the
     phone Filter | Sort bar sitting on top of it rather than behind it. */
  if (window.visualViewport) {
    var vpTimer = null;
    var placeBar = function () {
      clearTimeout(vpTimer);
      vpTimer = setTimeout(function () {
        var vv = window.visualViewport;
        var offset = Math.max(0, window.innerHeight - (vv.offsetTop + vv.height));
        root.style.setProperty('--np-toolbar-offset', offset + 'px');
      }, 80);
    };
    window.visualViewport.addEventListener('resize', placeBar);
    window.visualViewport.addEventListener('scroll', placeBar);
    placeBar();
  }

  /* THREE WAYS TO ARRIVE WITH ENGINE STATE, one outcome each.
     - On the engine page itself (a shared /apps/browse link, or the no-JS
       fallback): the address bar is rewritten to /collections/<handle>.
     - With the drawer's fragment (#np-facets-engine): the drawer opens the
       scripted way and the fragment goes.
     - On /collections/<handle> with filters in the URL (a shared filtered
       link, or Back from a product): Shopify drew the unfiltered grid, so
       the engine's is fetched in its place. */
  var panel = drawer();
  var wantsDrawer = panel && location.hash === '#' + panel.id;
  if (onEnginePath(location.href) && !isSearch() && root.dataset.storefrontUrl) {
    history.replaceState({ np: true }, '', toStorefront(location.href));
  } else if (wantsDrawer) {
    history.replaceState({ np: true }, '', location.pathname + location.search);
  }
  if (wantsDrawer) openDrawer(opener());
  var ready;
  if (!onEnginePath(location.href) && hasEngineState(location.href)) {
    ready = loadResults(toEngine(location.href), { replace: true });
  } else if (isViewAll(location.href)) {
    ready = viewAll();
  } else {
    ready = Promise.resolve();
  }

  /* Return-to-results, once the grid it measured is back — including every
     page of a "View all". */
  try {
    var stored = JSON.parse(sessionStorage.getItem('np:return') || 'null');
    if (stored && stored.url === location.href) {
      sessionStorage.removeItem('np:return');
      var loaded = new Promise(function (resolve) {
        if (document.readyState === 'complete') resolve();
        else window.addEventListener('load', resolve);
      });
      Promise.all([ready, loaded]).then(function () { window.scrollTo(0, stored.y); });
    }
  } catch (e) {}
})();
